/**
 * Which of a multimodal checkpoint's stacks is the one this single-stack model
 * came from — decided from the tensors, because the names are exactly what
 * cannot be trusted.
 *
 * ## The case
 *
 * `maglun/Gemma-4-E2B-it-Text-MLX-Mixed-3.62bpw` is `google/gemma-4-E2B-it`'s
 * text tower, extracted and quantized. It has `model.layers.N.…` where its
 * parent has `model.language_model.layers.N.…` beside a vision stack and an
 * audio stack. `compareScopeFor` used to match B's stack to A's by tower **id**,
 * which cannot work when A has no towers at all, so the comparison declined: "B
 * is a multi-stack model and A is not." Honest, and the end of the road.
 *
 * Name matching cannot fix that — differing names are the whole problem. Cell
 * signatures do not care what anything is called, which is the same property
 * that made them the right answer for the diff verdict (`inventory-diff.ts`),
 * and `cellSignature` is shared with it rather than reimplemented.
 *
 * ## Abstention, and why it has teeth
 *
 * A best-scoring tower is not automatically the right one. **A wrong tower
 * silently chosen is a confident wrong grid** — worse than the refusal it
 * replaces, because nothing on screen looks wrong. So two gates, and both are
 * *measured* rather than chosen: `docs/evidence/tower-match.md` scores 18 pairs
 * whose answers were written down first, and the values are read off the gap
 * between the populations. Re-running the corpus is part of changing them.
 *
 * ## Symmetric by construction
 *
 * The scorer takes **the single-stack side and the multi-stack side**, never A
 * and B. Compare mode can be pointed either way round — a visitor may load the
 * multimodal parent first and the extraction second — and a function that read
 * the argument order would be correct in one direction and silently wrong in
 * the other. `chooseTower` therefore has no notion of which side the visitor
 * typed first; `main.ts` works that out and hands the sides over already sorted.
 *
 * Pure: tensors in, a choice out. No DOM, no network, no clock. Costs nothing —
 * every input is a name and a shape already read from a header.
 */

import type { InventoryTensor } from "../safetensors/inventory.js";
import { cellSignature } from "./inventory-diff.js";
import { SHARED_LABEL, type Tower, detectTowers, towerOf } from "./towers.js";

/**
 * How much of the single-stack side a tower must account for.
 *
 * Measured, not chosen. It sits inside an empty band running from the best
 * wrong answer in the corpus (0.445 — `Qwen3-4B` against `Qwen3-VL-8B`'s text
 * tower) to the worst right one (0.996), so anything in that range separates
 * the corpus identically and this number is not tuned to a favourite example.
 *
 * It leans toward the cheaper failure. Refusing a correct tower restores the
 * old refusal, which was at least honest; accepting a wrong one draws a grid
 * that reads as authoritative and is not.
 */
export const TOWER_FLOOR = 0.8;

/**
 * How far the winner must be ahead of the runner-up.
 *
 * The gate exists for the case the floor cannot see: two stacks that are both
 * genuinely good matches, where picking the higher one is a coin flip dressed
 * as a measurement. That case is real and is in the corpus —
 * `sentence-transformers/sentence-t5-base` against `google-t5/t5-base` scores
 * **0.989 on each of encoder and decoder**, because a T5 decoder layer's
 * cross-attention classifies into the same roles, at the same layer indices,
 * with the same rows as its self-attention. The right answer is the encoder and
 * the shapes cannot say so, so the honest outcome is to decline.
 *
 * Measured on both sides with nothing in between: every correct pair in the
 * corpus has a runner-up of exactly 0.000 (unbounded margin), and the one
 * genuine tie has a margin of 1.000.
 */
export const TOWER_MARGIN = 2;

/** One stack's fit against the single-stack side. */
export interface TowerFit {
  readonly tower: Tower;
  /**
   * Shared cells ÷ **the single-stack side's** cells: how much of that model
   * this stack accounts for.
   *
   * Not `signatureOverlap`'s smaller-side ratio, and the difference is not
   * cosmetic. A multimodal checkpoint carries a synthetic `shared` bucket that
   * on three corpus pairs held exactly one cell; against the smaller side one
   * matching cell scores it 1.000, tying the real winner, and the margin gate
   * would then have **abstained on a correct pair** — the gate causing the
   * failure it exists to prevent. Dividing by the single side collapses that to
   * 0.003 with no special case. The verdict detector keeps the symmetric ratio
   * because it asks a symmetric question; one denominator per question.
   */
  readonly explains: number;
  /** Cells shared with the single-stack side — the evidence behind `explains`. */
  readonly shared: number;
  /** The single-stack side's cell count, so a caller can state the fraction. */
  readonly of: number;
}

export interface TowerChoice {
  /** The chosen stack, or `undefined` when the evidence did not clear a gate. */
  readonly tower: Tower | undefined;
  /** Every candidate, best first — present whether or not a choice was made. */
  readonly fits: readonly TowerFit[];
  /**
   * Why no tower was chosen, for a caller that wants to say so. `undefined`
   * exactly when `tower` is set.
   *
   * `"too-thin"` — nothing accounted for enough of the single side.
   * `"ambiguous"` — two stacks fit and the shapes cannot separate them.
   */
  readonly declined: "too-thin" | "ambiguous" | undefined;
}

/**
 * Score every stack of the multi-stack side against the whole single-stack side.
 *
 * The tower's tensors are filtered **before** the signature is built, which is
 * the same filter-then-fold order `anatomyOf` uses and is what keeps each
 * stack's layer indices its own. Classifying the whole checkpoint first would
 * put text layer 3, vision layer 3 and audio layer 3 into a single block and let
 * a sibling-gated rule reason across stacks that share nothing.
 */
export function chooseTower(
  single: readonly InventoryTensor[],
  multi: readonly InventoryTensor[],
): TowerChoice {
  const singleCells = cellSignature(single);
  const towers = detectTowers(multi.map((t) => t.name));

  const fits = towers
    /*
     * The synthetic bucket is not a candidate, on definitional grounds rather
     * than numeric ones. `towers.ts` calls it "the bucket for tensors that
     * belong to no single stack", so "A was extracted from `shared`" is not a
     * proposition, and the share card's §12.1 rule already refuses to land on
     * it while a real tower exists.
     *
     * Excluded from **candidacy only**. Its cells stay in `singleCells`, so any
     * of the single side that lands there counts against every score as
     * unexplained — which is the honest arithmetic, and the reason this is a
     * scope guard rather than a thumb on the scale.
     */
    .filter((tower) => tower.id !== SHARED_LABEL)
    .map((tower) => {
      const mine = multi.filter((t) => towerOf(t.name, towers) === tower.id);
      const cells = cellSignature(mine);
      let shared = 0;
      for (const cell of cells) if (singleCells.has(cell)) shared += 1;
      return {
        tower,
        shared,
        of: singleCells.size,
        explains: singleCells.size === 0 ? 0 : shared / singleCells.size,
      };
    })
    .sort((a, b) => b.explains - a.explains);

  const best = fits[0];
  const runnerUp = fits[1];
  if (best === undefined || best.explains < TOWER_FLOOR) {
    return { tower: undefined, fits, declined: "too-thin" };
  }
  /*
   * A runner-up of zero is an unbounded margin, not a division by zero — and
   * that is the common shape of a correct answer rather than an edge case:
   * every true positive in the corpus scores its runner-up at exactly 0.000,
   * because a vision stack has nothing in common with a text stack.
   */
  if (runnerUp !== undefined && runnerUp.explains * TOWER_MARGIN > best.explains) {
    return { tower: undefined, fits, declined: "ambiguous" };
  }
  return { tower: best.tower, fits, declined: undefined };
}
